// Moving assessment edits between the device and the server.
//
// A local record looks like:
//   {
//     uuid, formType, formCode, schemaVersion,
//     studentId, studentName, therapistId, therapistName,
//     server:        last form detail the server returned, or null if the form
//                    has not reached the server yet (started offline)
//     createPayload: { formType, studentId, therapistId, assessmentDate } while server is null
//     pending:       { key: { v, t } } edits not yet confirmed by the server
//     lockedNotice:  set when edits arrived after the form was submitted/cancelled
//     lastSyncedAt
//   }
//
// An edit leaves `pending` only once the server has answered a request that
// carried that exact edit (same key AND same timestamp). An edit typed while a
// sync is in flight therefore stays pending and goes in the next one.

import { assessmentApi } from './assessmentApi';
import { deleteForm, listForms, saveForm } from './assessmentStore';

export function newUuid() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const b = window.crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export function newLocalRecord({ formType, formCode, studentId, studentName, therapistId,
  therapistName, assessmentDate }) {
  return {
    uuid: newUuid(),
    formType,
    formCode,
    schemaVersion: null,
    studentId,
    studentName,
    therapistId,
    therapistName,
    server: null,
    createPayload: { formType: formCode, studentId, therapistId, assessmentDate },
    pending: {},
    lastSyncedAt: null,
  };
}

function metaFromDetail(detail) {
  return {
    uuid: detail.id,
    formType: detail.formType,
    formCode: detail.formCode,
    schemaVersion: detail.schemaVersion,
    studentId: detail.studentId,
    studentName: detail.studentName,
    therapistId: detail.therapistId,
    therapistName: detail.therapistName,
  };
}

export function recordFromServer(detail) {
  return {
    ...metaFromDetail(detail),
    server: detail,
    createPayload: undefined,
    pending: {},
    lastSyncedAt: Date.now(),
  };
}

export function currentValues(record) {
  const values = {};
  Object.entries(record?.server?.answers || {}).forEach(([k, e]) => { values[k] = e.v; });
  Object.entries(record?.pending || {}).forEach(([k, e]) => { values[k] = e.v; });
  return values;
}

export function pendingCount(record) {
  return Object.keys(record?.pending || {}).length + (record && !record.server ? 1 : 0);
}

export function isLocked(record) {
  const status = record?.server?.status;
  return status === 'submitted' || status === 'cancelled';
}

// Send whatever the device has that the server doesn't. `sent` is the exact
// snapshot of pending edits included in the request.
export async function pushRecord(record, sent) {
  if (!record.server) {
    return assessmentApi.create({ clientUuid: record.uuid, ...record.createPayload, answers: sent });
  }
  if (Object.keys(sent).length) {
    return assessmentApi.changes(record.uuid, sent);
  }
  return assessmentApi.get(record.uuid);
}

export function reconcile(record, sent, detail) {
  const pending = { ...(record.pending || {}) };
  const syncedKeys = [];
  Object.entries(sent).forEach(([key, e]) => {
    if (pending[key] && pending[key].t === e.t) delete pending[key];
    syncedKeys.push({ key, t: e.t });
  });
  return {
    ...record,
    ...metaFromDetail(detail),
    server: detail,
    createPayload: undefined,
    pending,
    syncedKeys,
    lastSyncedAt: Date.now(),
  };
}

// The form was submitted or cancelled while this device still had edits.
// The server keeps them in its change log (see routes/assessments.py), so
// they are not lost; the device stops retrying them.
export function lockRecord(record, body) {
  const count = Object.keys(record.pending || {}).length;
  return {
    ...record,
    server: record.server ? { ...record.server, status: body?.status || record.server.status } : record.server,
    syncedKeys: Object.entries(record.pending || {}).map(([key, e]) => ({ key, t: e.t })),
    pending: {},
    lockedNotice: count ? { count, at: Date.now() } : record.lockedNotice,
  };
}

// offline  -- never reached the server; retry later
// auth     -- token missing/expired; keep everything, ask to sign in
// locked   -- form is submitted/cancelled; edits archived server-side
// denied   -- not this user's form (404/403); stop
// error    -- server error; retry later
export function classifyError(err) {
  if (!err || err.status === 0) return 'offline';
  // flask-jwt-extended answers a malformed token with 422, not 401.
  if (err.status === 401 || (err.status === 422 && /token|segments|signature/i.test(err.message))) return 'auth';
  if (err.status === 409) return 'locked';
  if (err.status === 403 || err.status === 404) return 'denied';
  return 'error';
}

// Push every locally held form that has something the server lacks. Used by
// the list page and when the browser comes back online, so edits left behind
// in a closed tab still reach the server.
export async function syncPendingForms(userId) {
  let records;
  try {
    records = await listForms(userId);
  } catch {
    return { synced: 0, failed: 0 };
  }
  let synced = 0;
  let failed = 0;
  for (const record of records) {
    if (isLocked(record) && pendingCount(record) === 0) {
      await deleteForm(record.uuid).catch(() => {});
      continue;
    }
    if (pendingCount(record) === 0) continue;
    const sent = { ...record.pending };
    try {
      const detail = await pushRecord(record, sent);
      await saveForm(userId, reconcile(record, sent, detail));
      synced += 1;
    } catch (err) {
      const kind = classifyError(err);
      if (kind === 'locked') await saveForm(userId, lockRecord(record, err.body));
      failed += 1;
      if (kind === 'offline' || kind === 'auth') break;
    }
  }
  return { synced, failed };
}
